import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Modal,
  TextInput,
  Select,
  DatePicker,
  Tabs,
  Tag,
  Avatar,
  AvatarLabelGroup,
  Button,
  Badge,
  Alert,
  User01,
  Building07,
} from '@opendate-app/setlist';
import useAutoSave from '../../shared/components/useAutoSave';
import CollapsibleSection from '../../shared/components/CollapsibleSection';
import CrmCustomFieldsSection from '../../shared/components/CrmCustomFieldsSection';
import SidebarTagsPicker from '../../shared/components/SidebarTagsPicker';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';
import { getContactInitials, getStageLabel, statusOptions } from './dealStages';
import ContactQuickCreateForm from './ContactQuickCreateForm';
import crmLabels from '../../shared/utils/crmLabels';

const CONTACTS_ICON = '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>';
const NOTES_ICON = '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>';

const CONTACT_MENU_STYLE = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 8,
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: 8,
  boxShadow: '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03)',
  padding: '6px 0',
  backgroundColor: 'var(--sl-bg-primary)',
  zIndex: 1000,
  maxHeight: 320,
  overflowY: 'auto',
};

const CONTACT_HINT_STYLE = { padding: '6px 12px', color: 'var(--sl-text-quaternary)', fontSize: 13 };

const EMPTY_ARRAY = [];

function DealFormModal({
  isOpen,
  onClose,
  deal,
  team,
  csrfToken,
  stages = {},
  statuses,
  owners = [],
  currentOwnerId = null,
  campaigns = [],
  funds = EMPTY_ARRAY,
  organizations: initialOrganizations = EMPTY_ARRAY,
  selectedContacts = [],
  availableTags: initialAvailableTags = EMPTY_ARRAY,
  customFieldDefinitions = [],
  onOrganizationCreate,
  onTagCreate,
  onSaved,
}) {
  const isEditing = !!(deal && deal.id);
  const labels = crmLabels(team);
  const orgSingularLower = labels.organization.singular.toLowerCase();
  const orgPluralLower = labels.organization.plural.toLowerCase();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('sales');
  const [stage, setStage] = useState('lead');
  const [status, setStatus] = useState('');
  const [statusTouched, setStatusTouched] = useState(false);
  const [probability, setProbability] = useState('');
  const [committedAmount, setCommittedAmount] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextActionOn, setNextActionOn] = useState('');
  const [fundId, setFundId] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [primaryContactId, setPrimaryContactId] = useState('');
  const [additionalContactIds, setAdditionalContactIds] = useState([]);
  const [description, setDescription] = useState('');
  const { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes, resetCustomFieldValues, syncCustomFieldRowIds } = useCustomFieldValues(customFieldDefinitions, deal ? deal.custom_field_values : []);
  const [tags, setTags] = useState([]);

  const [organizations, setOrganizations] = useState(initialOrganizations);
  const [availableTags, setAvailableTags] = useState(initialAvailableTags);

  const [knownContacts, setKnownContacts] = useState({});
  const [primaryContactResults, setPrimaryContactResults] = useState([]);
  const [primaryContactsLoading, setPrimaryContactsLoading] = useState(false);
  const [additionalContactResults, setAdditionalContactResults] = useState([]);
  const [additionalContactsLoading, setAdditionalContactsLoading] = useState(false);
  const primarySearchTimerRef = useRef(null);
  const additionalSearchTimerRef = useRef(null);
  const primarySearchSeqRef = useRef(0);
  const additionalSearchSeqRef = useRef(0);

  const [primaryContactOpen, setPrimaryContactOpen] = useState(false);
  const [primaryContactSearch, setPrimaryContactSearch] = useState('');
  const [additionalContactsOpen, setAdditionalContactsOpen] = useState(false);
  const [additionalContactSearch, setAdditionalContactSearch] = useState('');

  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const orgSearchTimerRef = useRef(null);
  const orgSearchSeqRef = useRef(0);
  const orgRef = useRef(null);

  const [showNewOrgForm, setShowNewOrgForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSaving, setNewOrgSaving] = useState(false);

  const [newContactTarget, setNewContactTarget] = useState(null);
  const [newContactQuery, setNewContactQuery] = useState('');

  const primaryContactRef = useRef(null);
  const additionalContactsRef = useRef(null);

  const tagsRef = useRef([]);
  useEffect(() => { tagsRef.current = tags; }, [tags]);

  const handleSaved = useCallback(async (saved, isNew) => {
    syncCustomFieldRowIds(saved.custom_field_values);
    if (saved.status) setStatus(saved.status);
    setStatusTouched(false);
    if (isNew && tagsRef.current.length > 0) {
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        for (const tag of tagsRef.current) {
          await axios.post(`/teams/${team.id}/tagged_items.json`, {
            tagged_item: { taggable_id: saved.id, taggable_type: 'Deal', tag_id: tag.id },
          });
        }
      } catch (e) {
        toast.error('Failed to save tags.');
      }
    }
    if (onSaved) onSaved(saved, isNew);
  }, [onSaved, team, csrfToken]);

  const { recordId, saveStatus, errors, saveFields } = useAutoSave({
    team,
    csrfToken,
    resourcePath: 'crm/deals',
    resourceKey: 'deal',
    existingId: isEditing ? deal.id : null,
    isOpen,
    onSaved: handleSaved,
  });

  const searchContacts = useCallback(async (query, omitIds, setResults, setLoading, seqRef, seq) => {
    if (!team || !team.id) return;
    try {
      setLoading(true);
      const params = { name: query };
      if (omitIds.length > 0) params.omit_ids = omitIds;
      const response = await axios.get(`/teams/${team.id}/crm/fans/autocomplete`, { params });
      if (seq !== seqRef.current) return;
      const results = response.data || [];
      setResults(results);
      setKnownContacts((prev) => {
        const next = { ...prev };
        results.forEach((c) => { next[String(c.id)] = c; });
        return next;
      });
    } catch (e) {
      if (seq === seqRef.current) toast.error('Failed to search contacts.');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [team]);

  const searchOrganizations = useCallback(async (query, seq) => {
    if (!team || !team.id) return;
    try {
      setOrgSearchLoading(true);
      const response = await axios.get(`/teams/${team.id}/crm/organizations/search`, { params: { q: query } });
      if (seq !== orgSearchSeqRef.current) return;
      setOrgResults(response.data || []);
    } catch (e) {
      if (seq === orgSearchSeqRef.current) toast.error(`Failed to search ${orgPluralLower}.`);
    } finally {
      if (seq === orgSearchSeqRef.current) setOrgSearchLoading(false);
    }
  }, [team, orgPluralLower]);

  const selectedContactsRef = useRef(selectedContacts);
  selectedContactsRef.current = selectedContacts;

  useEffect(() => {
    if (!isOpen) return;

    // The modal stays mounted across open/close; re-init custom field state so
    // multi-association row ids don't go stale after a save.
    resetCustomFieldValues(deal && deal.id ? deal.custom_field_values : []);

    const map = {};
    (selectedContactsRef.current || []).forEach((c) => { map[String(c.id)] = c; });
    setKnownContacts(map);
    setPrimaryContactResults([]);
    setAdditionalContactResults([]);

    if (deal && deal.id) {
      setName(deal.name || '');
      setAmount(deal.amount ? String(deal.amount) : '');
      setCategory(deal.deal_type || 'sales');
      setStage(deal.stage || 'lead');
      setStatus(deal.status || '');
      setProbability(deal.probability != null ? String(deal.probability) : '');
      setCommittedAmount(deal.committed_amount != null ? String(deal.committed_amount) : '');
      setNextAction(deal.next_action || '');
      setNextActionOn(deal.next_action_on ? deal.next_action_on.substring(0, 10) : '');
      setFundId(deal.fund_id ? String(deal.fund_id) : '');
      setCloseDate(deal.close_date ? deal.close_date.substring(0, 10) : '');
      setOwnerId(deal.owner_team_membership_id ? String(deal.owner_team_membership_id) : '');
      setCampaignId(deal.giving_campaign_id ? String(deal.giving_campaign_id) : '');
      setOrganizationId(deal.organization_id ? String(deal.organization_id) : '');
      setPrimaryContactId(deal.primary_fan_id ? String(deal.primary_fan_id) : '');
      setAdditionalContactIds((deal.additional_fan_ids || []).map(String));
      setDescription(deal.description || '');
      setTags(deal.tags || []);
    } else {
      setName('');
      setAmount('');
      setCategory('sales');
      setStage('lead');
      setStatus('');
      setProbability('');
      setCommittedAmount('');
      setNextAction('');
      setNextActionOn('');
      setFundId('');
      setCloseDate('');
      setOwnerId(currentOwnerId ? String(currentOwnerId) : '');
      setCampaignId('');
      setOrganizationId('');
      setPrimaryContactId('');
      setAdditionalContactIds([]);
      setDescription('');
      setTags([]);
    }
    setStatusTouched(false);
    setShowNewOrgForm(false);
    setNewOrgName('');
    setNewContactTarget(null);
    setNewContactQuery('');
  }, [isOpen, deal, currentOwnerId]);

  useEffect(() => { setOrganizations(initialOrganizations); }, [initialOrganizations]);
  useEffect(() => { setAvailableTags(initialAvailableTags); }, [initialAvailableTags]);

  useEffect(() => {
    if (!orgDropdownOpen) return;
    const handleClick = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgDropdownOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [orgDropdownOpen]);

  useEffect(() => {
    if (!orgDropdownOpen) return undefined;
    const query = orgSearch.trim();
    const seq = orgSearchSeqRef.current + 1;
    orgSearchSeqRef.current = seq;
    if (!query) {
      setOrgResults([]);
      setOrgSearchLoading(false);
      return undefined;
    }
    setOrgResults([]);
    setOrgSearchLoading(true);
    if (orgSearchTimerRef.current) clearTimeout(orgSearchTimerRef.current);
    orgSearchTimerRef.current = setTimeout(() => { searchOrganizations(query, seq); }, 300);
    return () => clearTimeout(orgSearchTimerRef.current);
  }, [orgDropdownOpen, orgSearch, searchOrganizations]);

  useEffect(() => {
    if (!primaryContactOpen) return;
    const handleClick = (e) => {
      if (primaryContactRef.current && !primaryContactRef.current.contains(e.target)) setPrimaryContactOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [primaryContactOpen]);

  useEffect(() => {
    if (!additionalContactsOpen) return;
    const handleClick = (e) => {
      if (additionalContactsRef.current && !additionalContactsRef.current.contains(e.target)) setAdditionalContactsOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [additionalContactsOpen]);

  useEffect(() => {
    if (!primaryContactOpen) return;
    const seq = primarySearchSeqRef.current + 1;
    primarySearchSeqRef.current = seq;
    if (primarySearchTimerRef.current) clearTimeout(primarySearchTimerRef.current);
    primarySearchTimerRef.current = setTimeout(() => {
      searchContacts(primaryContactSearch, [], setPrimaryContactResults, setPrimaryContactsLoading, primarySearchSeqRef, seq);
    }, primaryContactSearch ? 300 : 0);
    return () => clearTimeout(primarySearchTimerRef.current);
  }, [primaryContactOpen, primaryContactSearch, searchContacts]);

  useEffect(() => {
    if (!additionalContactsOpen) return;
    const seq = additionalSearchSeqRef.current + 1;
    additionalSearchSeqRef.current = seq;
    if (additionalSearchTimerRef.current) clearTimeout(additionalSearchTimerRef.current);
    const omitIds = [primaryContactId, ...additionalContactIds].filter(Boolean).map((id) => parseInt(id, 10));
    additionalSearchTimerRef.current = setTimeout(() => {
      searchContacts(additionalContactSearch, omitIds, setAdditionalContactResults, setAdditionalContactsLoading, additionalSearchSeqRef, seq);
    }, additionalContactSearch ? 300 : 0);
    return () => clearTimeout(additionalSearchTimerRef.current);
  }, [additionalContactsOpen, additionalContactSearch, searchContacts, primaryContactId, additionalContactIds]);

  if (!isOpen) return null;

  const canSubmitForm = name.trim().length > 0 && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv));

  const isFundraising = category === 'fundraising';
  const givingHidden = !!team?.can_access_giving_crm_boundary && !team?.can_access_giving_campaigns;

  const buildPayload = () => ({
    name,
    amount: amount || null,
    stage,
    ...(statusTouched ? { status: status || null } : {}),
    probability: probability === '' ? null : Number(probability),
    committed_amount: committedAmount || null,
    next_action: nextAction || null,
    next_action_on: nextActionOn || null,
    fund_id: isFundraising ? (fundId || null) : null,
    close_date: closeDate || null,
    deal_type: category,
    owner_team_membership_id: ownerId || null,
    giving_campaign_id: campaignId || null,
    organization_id: organizationId || null,
    primary_fan_id: primaryContactId || null,
    description: description || null,
    additional_fan_ids: additionalContactIds.map((id) => parseInt(id, 10)),
    custom_field_values_attributes: buildCustomFieldAttributes(),
  });

  const handleClose = () => {
    if (saveStatus !== 'saving') onClose();
  };

  const handleSubmit = () => {
    if (!canSubmitForm || saveStatus === 'saving') return;
    saveFields(buildPayload(), { afterSave: onClose });
  };

  const stagesForCategory = (stages && stages[category]) || [];

  const handleCategoryChange = (next) => {
    if (next === category) return;
    setCategory(next);
    const allowed = (stages && stages[next]) || [];
    if (!allowed.includes(stage)) {
      setStage(allowed[0] || '');
      if (!statusTouched) setStatus('');
    }
  };

  const getContactById = (contactId) => {
    if (!contactId) return null;
    return knownContacts[String(contactId)] || null;
  };

  const getContactName = (contactId) => {
    const contact = getContactById(contactId);
    return contact ? contact.name : null;
  };

  const getOrganizationName = (orgId) => {
    if (!orgId) return null;
    const match = organizations.find((o) => String(o.id) === String(orgId));
    if (match) return match.name;
    if (deal && String(deal.organization_id) === String(orgId)) return deal.organization_name || null;
    return null;
  };

  const orgOptions = orgSearch.trim() ? orgResults : organizations;

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim() || !team || !team.id) return;
    try {
      setNewOrgSaving(true);
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.post(`/teams/${team.id}/crm/organizations.json`, {
        organization: { name: newOrgName.trim() },
      });
      const newOrg = response.data;
      const updated = [...organizations, { id: newOrg.id, name: newOrg.name }].sort((a, b) => a.name.localeCompare(b.name));
      setOrganizations(updated);
      setOrganizationId(String(newOrg.id));
      setShowNewOrgForm(false);
      setNewOrgName('');
      setOrgDropdownOpen(false);
      if (onOrganizationCreate) onOrganizationCreate(newOrg);
    } catch (e) {
      toast.error(`Failed to create ${orgSingularLower}.`);
    } finally {
      setNewOrgSaving(false);
    }
  };

  const handleSelectPrimaryContact = (contact) => {
    setKnownContacts((prev) => ({ ...prev, [String(contact.id)]: contact }));
    setPrimaryContactId(String(contact.id));
    setPrimaryContactSearch('');
    setPrimaryContactOpen(false);
  };

  const handleClearPrimaryContact = () => {
    setPrimaryContactId('');
  };

  const handleAddAdditionalContact = (contact) => {
    setKnownContacts((prev) => ({ ...prev, [String(contact.id)]: contact }));
    setAdditionalContactIds((prev) => [...prev, String(contact.id)]);
    setAdditionalContactSearch('');
  };

  const openNewContactForm = (target, query) => {
    setNewContactTarget(target);
    setNewContactQuery(query.trim());
    setPrimaryContactOpen(false);
    setAdditionalContactsOpen(false);
  };

  const closeNewContactForm = () => {
    setNewContactTarget(null);
    setNewContactQuery('');
  };

  const handleContactCreated = (fan) => {
    if (newContactTarget === 'primary') {
      handleSelectPrimaryContact(fan);
    } else {
      handleAddAdditionalContact(fan);
    }
    closeNewContactForm();
  };

  const renderCreateContactOption = (target, query) => (
    <button
      type="button"
      className="cfm-contact-option"
      style={{ color: 'var(--sl-fg-brand)' }}
      onClick={() => openNewContactForm(target, query)}
    >
      {query.trim() ? `+ Create contact "${query.trim()}"` : '+ Create new contact'}
    </button>
  );

  const renderNewContactForm = (target) => newContactTarget === target && (
    <ContactQuickCreateForm
      key={`${target}-${newContactQuery}`}
      team={team}
      csrfToken={csrfToken}
      initialQuery={newContactQuery}
      onCreated={handleContactCreated}
      onCancel={closeNewContactForm}
    />
  );

  const handleRemoveAdditionalContact = (contactId) => {
    setAdditionalContactIds((prev) => prev.filter((id) => id !== String(contactId)));
  };

  const handleAddTag = async (tag) => {
    if (tags.find((t) => t.id === tag.id)) return;
    setTags((prev) => [...prev, tag]);
    const dealId = recordId;
    if (dealId) {
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        await axios.post(`/teams/${team.id}/tagged_items.json`, {
          tagged_item: { taggable_id: dealId, taggable_type: 'Deal', tag_id: tag.id },
        });
      } catch (e) {
        toast.error('Failed to add tag.');
      }
    }
  };

  const handleRemoveTag = async (tagId) => {
    setTags((prev) => prev.filter((t) => t.id !== tagId));
    const dealId = recordId;
    if (dealId) {
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        const resp = await axios.get(`/teams/${team.id}/tagged_items.json`, {
          params: { taggable_id: dealId, taggable_type: 'Deal' },
        });
        const taggedItem = (resp.data || []).find((ti) => ti.tag_id === tagId);
        if (taggedItem) {
          await axios.delete(`/teams/${team.id}/tagged_items/${taggedItem.id}.json`);
        }
      } catch (e) {
        toast.error('Failed to remove tag.');
      }
    }
  };

  const handleCreateTag = async (tagName) => {
    if (!team || !team.id || !csrfToken || !tagName.trim()) return;
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.post(`/teams/${team.id}/crm/tags`, {
        tag: { name: tagName.trim(), scope: 'Deal' },
      });
      const newTag = response.data;
      setAvailableTags((prev) => [...prev, { id: newTag.id, name: newTag.name }].sort((a, b) => a.name.localeCompare(b.name)));
      setTags((prev) => [...prev, { id: newTag.id, name: newTag.name }]);
      const dealId = recordId;
      if (dealId) {
        await axios.post(`/teams/${team.id}/tagged_items.json`, {
          tagged_item: { taggable_id: dealId, taggable_type: 'Deal', tag_id: newTag.id },
        });
      }
      if (onTagCreate) onTagCreate(newTag);
    } catch (e) {
      toast.error('Failed to create tag.');
    }
  };

  const saveStatusLabel = {
    idle: null,
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Error saving',
  }[saveStatus];

  const saveStatusColor = {
    saving: 'gray',
    saved: 'success',
    error: 'error',
  }[saveStatus] || 'gray';

  const breadcrumbLabel = name.trim() ? name.trim() : (isEditing ? `Edit ${labels.deal.singular}` : `Add ${labels.deal.singular}`);

  const toDateValue = (isoDate) => (isoDate ? new Date(`${isoDate}T00:00:00`) : null);
  const handleDateChange = (setter) => (date) => {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      setter(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    } else {
      setter('');
    }
  };

  const renderContactOption = (contact, onSelect) => (
    <button
      key={contact.id}
      type="button"
      className="cfm-contact-option"
      onClick={() => onSelect(contact)}
    >
      <AvatarLabelGroup
        size="sm"
        initials={getContactInitials(contact)}
        name={contact.name || contact.email || contact.title_email_preview || 'Unnamed contact'}
        supportingText={contact.name ? (contact.email || contact.title_email_preview || '') : ''}
      />
    </button>
  );

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
      {saveStatusLabel && <Badge color={saveStatusColor}>{saveStatusLabel}</Badge>}
      <Button
        variant="primary"
        disabled={!canSubmitForm || saveStatus === 'saving'}
        onClick={handleSubmit}
      >
        {saveStatus === 'saving' ? 'Saving…' : `Save ${labels.deal.singular.toLowerCase()}`}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={handleClose}
      icon={false}
      layout="stacked"
      width={920}
      title={breadcrumbLabel}
      footer={footer}
    >
      {errors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Please fix the following" description={errors.join(', ')} />
        </div>
      )}

      <div className="cfm-body" style={{ maxHeight: '68vh' }}>
        <div className="cfm-pane-main">
          {!givingHidden && (
            <div className="cfm-field">
              <label className="cfm-label">Category</label>
              <Tabs
                variant="button-border"
                aria-label={`${labels.deal.singular} category`}
                value={category}
                onChange={handleCategoryChange}
                items={[
                  { value: 'sales', label: 'Sales' },
                  { value: 'fundraising', label: 'Fundraising' },
                ]}
              />
              <div className="figma-text tertiary xs" style={{ marginTop: 6 }}>
                {isFundraising
                  ? `Fundraising ${labels.deal.plural.toLowerCase()} use cultivation stages and roll up to a giving campaign.`
                  : `Sales ${labels.deal.plural.toLowerCase()} track opportunities through your sales pipeline.`}
              </div>
            </div>
          )}

          <div className="cfm-field">
            <TextInput
              label={`${labels.deal.singular} name`}
              required
              placeholder={`${labels.deal.singular} name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="cfm-field" ref={orgRef} style={{ position: 'relative' }}>
            <label className="cfm-label">{labels.organization.singular}</label>
            <TextInput
              leadingIcon={Building07}
              placeholder={`Search ${orgSingularLower}…`}
              value={orgDropdownOpen ? orgSearch : (getOrganizationName(organizationId) || '')}
              onChange={(e) => setOrgSearch(e.target.value)}
              onFocus={() => { setOrgDropdownOpen(true); setOrgSearch(''); }}
            />
            {orgDropdownOpen && (
              <div style={CONTACT_MENU_STYLE}>
                <button
                  type="button"
                  className="cfm-contact-option"
                  style={{ color: 'var(--sl-fg-brand)' }}
                  onClick={() => { setShowNewOrgForm(true); setOrgDropdownOpen(false); }}
                >
                  {`+ Create new ${orgSingularLower}`}
                </button>
                {organizationId && (
                  <button
                    type="button"
                    className="cfm-contact-option"
                    style={{ color: 'var(--sl-text-quaternary)' }}
                    onClick={() => { setOrganizationId(''); setOrgSearch(''); setOrgDropdownOpen(false); }}
                  >
                    Clear selection
                  </button>
                )}
                {orgSearchLoading && <div style={CONTACT_HINT_STYLE}>Searching…</div>}
                {!orgSearchLoading && orgOptions.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    className="cfm-contact-option"
                    onClick={() => {
                      setOrganizations((prev) => (prev.some((o) => String(o.id) === String(org.id)) ? prev : [...prev, { id: org.id, name: org.name }]));
                      setOrganizationId(String(org.id));
                      setOrgSearch('');
                      setOrgResults([]);
                      setOrgDropdownOpen(false);
                    }}
                  >
                    <span className="cfm-contact-name">{org.name}</span>
                  </button>
                ))}
                {!orgSearchLoading && orgOptions.length === 0 && (
                  <div style={CONTACT_HINT_STYLE}>
                    {orgSearch.trim() ? `No ${orgPluralLower} found` : `Type to search ${orgPluralLower}`}
                  </div>
                )}
              </div>
            )}
            {showNewOrgForm && (
              <div style={{ padding: 12, backgroundColor: 'var(--sl-bg-tertiary)', border: '1px solid var(--sl-border-secondary)', borderRadius: 8, marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      size="sm"
                      label={`New ${orgSingularLower}`}
                      placeholder={`${labels.organization.singular} name`}
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCreateOrganization}
                    disabled={!newOrgName.trim() || newOrgSaving}
                  >
                    {newOrgSaving ? 'Creating…' : 'Create'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setShowNewOrgForm(false); setNewOrgName(''); }}
                    disabled={newOrgSaving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="cfm-field-row-2col">
            <div className="cfm-field">
              <TextInput
                label={isFundraising ? 'Ask amount' : 'Amount'}
                leadingText="$"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              />
            </div>
            <div className="cfm-field">
              <DatePicker
                label="Close date"
                placeholder="Select date"
                value={toDateValue(closeDate)}
                onChange={handleDateChange(setCloseDate)}
              />
            </div>
          </div>

          <div className="cfm-field-row-2col">
            <div className="cfm-field">
              <TextInput
                label="Probability"
                trailingText="%"
                inputMode="numeric"
                placeholder="0–100"
                value={probability}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                  setProbability(digits && Number(digits) > 100 ? '100' : digits);
                }}
              />
            </div>
            <div className="cfm-field">
              <TextInput
                label="Committed amount"
                leadingText="$"
                placeholder="0"
                value={committedAmount}
                onChange={(e) => setCommittedAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              />
            </div>
          </div>

          <div className="cfm-field-row-2col">
            <div className="cfm-field">
              <TextInput
                label="Next action"
                placeholder="e.g. Send proposal"
                maxLength={255}
                value={nextAction}
                onChange={(e) => setNextAction(e.target.value)}
              />
            </div>
            <div className="cfm-field">
              <DatePicker
                label="Next action date"
                placeholder="Select date"
                value={toDateValue(nextActionOn)}
                onChange={handleDateChange(setNextActionOn)}
              />
            </div>
          </div>

          <CollapsibleSection title={labels.contact.plural} icon={CONTACTS_ICON} defaultExpanded={false}>
            <div className="cfm-field" ref={primaryContactRef} style={{ position: 'relative' }}>
              <TextInput
                label={`Primary ${labels.contact.singular}`}
                leadingIcon={User01}
                placeholder={`Select a ${labels.contact.singular.toLowerCase()}`}
                value={primaryContactOpen ? primaryContactSearch : (getContactName(primaryContactId) || '')}
                onChange={(e) => setPrimaryContactSearch(e.target.value)}
                onFocus={() => { setPrimaryContactOpen(true); setPrimaryContactSearch(''); }}
              />
              {primaryContactOpen && (
                <div style={CONTACT_MENU_STYLE}>
                  {primaryContactId && (
                    <button
                      type="button"
                      className="cfm-contact-option"
                      style={{ color: 'var(--sl-text-quaternary)' }}
                      onClick={() => { handleClearPrimaryContact(); setPrimaryContactOpen(false); }}
                    >
                      Clear selection
                    </button>
                  )}
                  {primaryContactsLoading && <div style={CONTACT_HINT_STYLE}>Searching…</div>}
                  {!primaryContactsLoading && primaryContactResults.map((contact) => renderContactOption(contact, handleSelectPrimaryContact))}
                  {!primaryContactsLoading && primaryContactResults.length === 0 && (
                    <div style={CONTACT_HINT_STYLE}>No contacts found</div>
                  )}
                  {renderCreateContactOption('primary', primaryContactSearch)}
                </div>
              )}
              {renderNewContactForm('primary')}
            </div>

            <div className="cfm-field" ref={additionalContactsRef} style={{ position: 'relative', marginTop: 12 }}>
              <label className="cfm-label">Additional Contacts</label>
              {additionalContactIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {additionalContactIds.map((cId) => {
                    const c = getContactById(cId);
                    return (
                      <Tag
                        key={cId}
                        size="sm"
                        avatar={<Avatar size="xs" initials={getContactInitials(c)} alt={c?.name} />}
                        onDismiss={() => handleRemoveAdditionalContact(cId)}
                      >
                        {c?.name || 'Unknown'}
                      </Tag>
                    );
                  })}
                </div>
              )}
              <TextInput
                leadingIcon={User01}
                placeholder="Add additional contacts"
                value={additionalContactSearch}
                onChange={(e) => setAdditionalContactSearch(e.target.value)}
                onFocus={() => setAdditionalContactsOpen(true)}
              />
              {additionalContactsOpen && (
                <div style={CONTACT_MENU_STYLE}>
                  {additionalContactsLoading && <div style={CONTACT_HINT_STYLE}>Searching…</div>}
                  {!additionalContactsLoading && additionalContactResults.map((contact) => renderContactOption(contact, handleAddAdditionalContact))}
                  {!additionalContactsLoading && additionalContactResults.length === 0 && (
                    <div style={CONTACT_HINT_STYLE}>No contacts found</div>
                  )}
                  {renderCreateContactOption('additional', additionalContactSearch)}
                </div>
              )}
              {renderNewContactForm('additional')}
            </div>
            <div style={{ height: 32 }} />
          </CollapsibleSection>

          <CrmCustomFieldsSection
            team={team}
            customFieldDefinitions={customFieldDefinitions}
            customFieldValues={customFieldValues}
            onCustomFieldChange={handleCustomFieldChange}
          />

          <CollapsibleSection title="Notes" icon={NOTES_ICON} defaultExpanded={false}>
            <div className="cfm-field">
              <TextInput
                label="Description"
                multiline
                rows={3}
                placeholder={`Add notes about this ${labels.deal.singular.toLowerCase()}…`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </CollapsibleSection>
        </div>

        <div className="cfm-pane-sidebar">
          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created by</span>
            <span className="cfm-prop-value-static">You</span>
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Stage</span>
            <Select
              size="sm"
              aria-label="Stage"
              options={stagesForCategory.map((s) => ({ value: s, label: getStageLabel(s) }))}
              value={stage || null}
              onChange={(value) => {
                setStage(value || '');
                if (!statusTouched) setStatus('');
              }}
            />
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Status</span>
            <Select
              size="sm"
              aria-label="Status"
              options={statusOptions(statuses)}
              placeholder="Set from stage"
              value={status || null}
              onChange={(value) => { setStatus(value || ''); setStatusTouched(true); }}
            />
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Owner</span>
            <Select
              size="sm"
              aria-label="Owner"
              placeholder="Unassigned"
              clearable
              options={owners.map((o) => ({ value: String(o.value), label: o.label }))}
              value={ownerId || null}
              onChange={(value) => setOwnerId(value || '')}
            />
          </div>

          {!givingHidden && (
            <div className="cfm-prop-row cfm-prop-row--stacked">
              <span className="cfm-prop-label">Campaign</span>
              <Select
                size="sm"
                aria-label="Campaign"
                placeholder="None"
                clearable
                options={campaigns.map((c) => ({ value: String(c.value), label: c.label }))}
                value={campaignId || null}
                onChange={(value) => setCampaignId(value || '')}
              />
            </div>
          )}

          {isFundraising && !givingHidden && (
            <div className="cfm-prop-row cfm-prop-row--stacked">
              <span className="cfm-prop-label">Fund</span>
              <Select
                size="sm"
                aria-label="Fund"
                placeholder="None"
                clearable
                options={funds.map((f) => ({ value: String(f.value), label: f.label }))}
                value={fundId || null}
                onChange={(value) => setFundId(value || '')}
              />
            </div>
          )}

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Priority</span>
            <Select size="sm" aria-label="Priority" placeholder="Select…" disabled options={[]} value={null} />
          </div>

          <div className="cfm-prop-row" style={{ alignItems: 'flex-start' }}>
            <span className="cfm-prop-label" style={{ paddingTop: 4 }}>Tags</span>
            <SidebarTagsPicker
              tags={tags}
              availableTags={availableTags}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
              onCreateTag={handleCreateTag}
            />
          </div>

          <div className="cfm-sidebar-divider" />
          <div className="cfm-sidebar-section-label">Record Info</div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created</span>
            <span className="cfm-prop-value-static">
              {deal && deal.created_at ? new Date(deal.created_at).toLocaleDateString() : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Modified</span>
            <span className="cfm-prop-value-static">
              {deal && deal.updated_at ? new Date(deal.updated_at).toLocaleDateString() : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">{labels.contact.plural}</span>
            <span className="cfm-prop-value-static">
              {(primaryContactId ? 1 : 0) + additionalContactIds.length}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default DealFormModal;
